import { directionsUrl, staticMap, TILE_ATTRIBUTION } from "../../lib/maps";
import { useI18n } from "../../lib/i18n";

// The club page's map, as a picture rather than an application.
//
// What this replaces: Leaflet, a lazily-loaded chunk that also brought its own
// stacking context. Leaflet puts its controls at z-index 800 and its corners at
// 1000; the sticky header sits at 40, so the map drew straight over the
// navigation. That was fixable, but the better fix was to ask what the map is
// for. On a detail page it answers one question, "where is this", and nobody
// pans a road map of a town they have never been to. The catalogue map, where
// panning is the whole point, stays interactive.
//
// No script, no library, no cost: the same free CARTO tiles, four images, laid
// out with CSS and rendered server-side.
export function StaticClubMap({
  lat,
  lng,
  name,
  className = "",
}: {
  lat: number;
  lng: number;
  name: string;
  className?: string;
}) {
  const { t } = useI18n();
  const map = staticMap(lat, lng, { width: 640, height: 320, zoom: 15 });

  return (
    // isolate, so nothing in here can ever paint over the header again,
    // whatever it grows into later.
    <div
      className={`group relative isolate aspect-[2/1] w-full overflow-hidden rounded-xl border border-[#1C2430]/10 bg-[#E7E2D6] ${className}`}
    >
      {/* The tile canvas, drawn at its natural size and centred, so the
          container simply crops it. Scaling would need the container's width,
          which needs either JavaScript or container queries; cropping needs
          neither and keeps the pin dead centre at every viewport. A narrow
          phone sees less map around the same point, which is the right thing
          to lose. */}
      {/* Centred with negative margins rather than a translate. That looks like
          a stylistic choice and is not: measured in Chrome, a transformed
          ancestor stops `loading="lazy"` from ever firing, so all eight tiles
          stayed at naturalWidth 0 and the map rendered as an empty beige box.
          Margins are layout, so the lazy-load observer sees the real position
          and the tiles arrive when they scroll into view. */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: map.width,
          height: map.height,
          marginLeft: -map.width / 2,
          marginTop: -map.height / 2,
        }}
      >
        {map.tiles.map((tile) => (
          <img
            key={tile.url}
            src={tile.url}
            alt=""
            aria-hidden="true"
            width={256}
            height={256}
            loading="lazy"
            decoding="async"
            // The hero photo is what the visitor came for; the map can wait.
            fetchPriority="low"
            draggable={false}
            className="absolute max-w-none select-none"
            style={{ left: tile.left, top: tile.top, width: 256, height: 256 }}
          />
        ))}

        {/* The pin, drawn rather than loaded: no request, crisp at any size. */}
        <span
          aria-hidden="true"
          className="absolute -translate-x-1/2 -translate-y-full drop-shadow-[0_2px_4px_rgba(10,20,35,0.45)]"
          style={{ left: map.pin.left, top: map.pin.top }}
        >
          <svg viewBox="0 0 24 32" className="h-8 w-6">
            <path
              d="M12 0C5.4 0 0 5.3 0 11.9 0 20.6 12 32 12 32s12-11.4 12-20.1C24 5.3 18.6 0 12 0z"
              fill="#2C4FD6"
            />
            <circle cx="12" cy="11.5" r="4.4" fill="#F2EFE9" />
          </svg>
        </span>
      </div>

      {/* The whole map is the route link, but as an overlay rather than a
          wrapper: the attribution below carries its own links, and an anchor
          inside an anchor is invalid HTML that the parser silently unnests. */}
      <a
        href={directionsUrl(lat, lng)}
        target="_blank"
        rel="noreferrer"
        className="absolute inset-0 flex items-start p-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-[#2C4FD6]"
        aria-label={`${t("get_directions")}: ${name}`}
      >
        <span className="li-mono rounded-lg bg-[#1C2430] px-3 py-1.5 text-[12px] text-[#F2EFE9] transition-colors group-hover:bg-[#2C4FD6]">
          {t("get_directions")}
        </span>
      </a>

      <span
        className="li-mono absolute bottom-0 right-0 bg-[#F2EFE9]/85 px-1.5 py-0.5 text-[9px] text-[#5B6472] [&_a]:underline"
        // The tile licence requires the credit, and it must stay clickable, so
        // it sits above the route overlay rather than under it.
        dangerouslySetInnerHTML={{ __html: TILE_ATTRIBUTION }}
      />
    </div>
  );
}
